import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getCustomers } from "../../../store/AsyncMethods/CustomerMethod";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { motion } from "framer-motion";
import moment from "moment";
import * as Yup from "yup";
import CustomerSalesReportsTable from "./CustomerSalesReportsTable";

export default function CustomerStatement() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [expandedReport, setExpandedReport] = useState(true);
  const [values, setValues] = useState({});
  const { allCustomers } = useSelector((state) => state.CustomersReducer);

  const formatDate = (date) =>
    date ? moment(date).format("YYYY-MM-DD") : null;

  useEffect(() => {
    if (Object.keys(values).length) {
      formik.setValues({
        from_date: new Date(values?.from_date),
        to_date: new Date(values?.to_date),
        customer_id: values?.customer_id || [],
      });
      setExpandedReport(false);
      setExpanded(true);
    }
  }, [values]);

  useEffect(() => {
    dispatch(getCustomers());
  }, [dispatch]);

  const formik = useFormik({
    initialValues: {
      from_date: new Date(new Date().getFullYear(), 0, 1),
      to_date: new Date(),
      customer_id: [],
    },
    validationSchema: Yup.object({
      from_date: Yup.string().required("From Date Required"),
      to_date: Yup.string().required("To Date Required"),
      customer_id: Yup.array().min(1, "At least one customer must be selected"),
    }),
    onSubmit: (values) => {
      setValues({
        ...values,
        from_date: formatDate(values.from_date),
        to_date: formatDate(values.to_date),
      });
    },
  });

  return (
    <div className="mx-4 rounded-lg bg-white">
      <form onSubmit={formik.handleSubmit}>
        <div className="card shadow-md rounded-lg p-4 mt-4">
          <Divider
            onClick={() => setExpandedReport(!expandedReport)}
            className="cursor-pointer"
          >
            <span className="text-2xl font-bold p-font-style text-primary">
              Customer Statement Report
            </span>
          </Divider>

          <motion.div
            animate={{ height: expandedReport ? "fit-content" : 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {/* From Date */}
              <div className="flex flex-col gap-2">
                <label htmlFor="from_date">{t("from_date")}</label>
                <Calendar
                  id="from_date"
                  name="from_date"
                  value={
                    formik.values.from_date
                      ? new Date(formik.values.from_date)
                      : null
                  }
                  onChange={(e) =>
                    formik.setFieldValue("from_date", e.value || null)
                  }
                  onBlur={formik.handleBlur}
                  className="w-full calender p-primary-cld-btn"
                  showIcon
                />
                {formik.touched.from_date && formik.errors.from_date && (
                  <div className="p-error">{formik.errors.from_date}</div>
                )}
              </div>

              {/* To Date */}
              <div className="flex flex-col gap-2">
                <label htmlFor="to_date">{t("to_date")}</label>
                <Calendar
                  id="to_date"
                  name="to_date"
                  value={
                    formik.values.to_date
                      ? new Date(formik.values.to_date)
                      : null
                  }
                  onChange={(e) =>
                    formik.setFieldValue("to_date", e.value || null)
                  }
                  onBlur={formik.handleBlur}
                  className="w-full calender p-primary-cld-btn"
                  showIcon
                />
                {formik.touched.to_date && formik.errors.to_date && (
                  <div className="p-error">{formik.errors.to_date}</div>
                )}
              </div>

              {/* Customer Selection */}
              <div className="flex flex-col gap-2">
                <label htmlFor="customer_id">{t("customer_name")}</label>
                <MultiSelect
                  id="customer_id"
                  name="customer_id"
                  value={formik.values.customer_id}
                  onChange={(e) => {
                    console.log("e ----->", e);
                    formik.setFieldValue("customer_id", e.target.value);
                  }}
                  onBlur={formik.handleBlur}
                  options={allCustomers}
                  optionLabel="name"
                  filter
                  placeholder="Select Customers"
                  className="w-full p-primary-input"
                />
                {formik.touched.customer_id && formik.errors.customer_id && (
                  <div className="p-error">{formik.errors.customer_id}</div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-16 flex justify-end gap-4">
              <Button
                label={t("cancel")}
                icon="pi pi-times"
                className="p-red-btn"
                type="button"
                onClick={formik.resetForm}
              />
              <Button
                label={t("submit")}
                icon="pi pi-check"
                className="p-secondary-btn"
                disabled={
                  !formik.values.customer_id?.length ||
                  !formik.values.from_date ||
                  !formik.values.to_date ||
                  !formik.isValid
                }
              />
            </div>
          </motion.div>
        </div>
      </form>

      {expanded && (
        <div className="card mt-10">
          <div className="flex h-full">
            <button
              onClick={() => setExpandedReport(!expandedReport)}
              className="background-clr-primary text-white font-bold p-font-style py-2 px-4 rounded-l"
            >
              <i className="pi pi-angle-double-left"></i>
            </button>
            <div className="h-1 w-full background-clr-primary"></div>
            <button
              onClick={() => setExpandedReport(!expandedReport)}
              className="background-clr-primary text-white font-bold p-font-style py-2 px-4 rounded-r"
            >
              <i className="pi pi-angle-double-left"></i>
            </button>
          </div>
          <CustomerSalesReportsTable values={values} />
        </div>
      )}
    </div>
  );
}
